import { useState } from "react";
import { Check, Copy, Link2, LogOut, Loader2, ShieldCheck, ShieldOff, UserCheck } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";

export const SecurityBadges = ({ u }) => (
  <div className="flex flex-wrap items-center gap-1.5">
    {u.totp_enabled
      ? <span className="inline-flex items-center gap-1 rounded-full bg-emerald-50 px-2 py-0.5 text-[10px] font-medium text-emerald-700" data-testid={`admin-2fa-on-${u.id}`}><ShieldCheck size={10} />2FA on</span>
      : <span className="inline-flex items-center gap-1 rounded-full bg-zinc-100 px-2 py-0.5 text-[10px] text-zinc-500" data-testid={`admin-2fa-off-${u.id}`}><ShieldOff size={10} />No 2FA</span>}
    <span className="rounded-full bg-zinc-100 px-2 py-0.5 text-[10px] text-zinc-500" data-testid={`admin-sessions-${u.id}`}>{u.sessions || 0} active session{u.sessions === 1 ? "" : "s"}</span>
    {u.signup_device && <span className="text-[10px] text-zinc-400">joined from {u.signup_device}</span>}
  </div>
);

export const ApprovalBanner = ({ u, onUpdate }) => (
  <div className="mt-4 flex flex-wrap items-center gap-3 rounded-md bg-sky-50 px-3 py-2" data-testid={`admin-approval-${u.id}`}>
    <p className="text-[11px] text-sky-900">Signed up {u.signup_device ? `from ${u.signup_device}` : ""}{u.signup_ip ? ` (${u.signup_ip})` : ""} and is waiting for approval. Until you approve, they can't sign in.</p>
    <button onClick={() => onUpdate(u, { approved: true })} data-testid={`admin-approve-${u.id}`} className="flex h-7 items-center gap-1 rounded-full bg-sky-700 px-3 text-[11px] font-medium text-white hover:bg-sky-800"><UserCheck size={11} />Approve</button>
  </div>
);

/* Owner-side actions: one-time reset link (no email needed) and sign the user out everywhere. */
export const AdminSecurityActions = ({ u }) => {
  const [link, setLink] = useState(null);
  const [busy, setBusy] = useState("");
  const [copied, setCopied] = useState(false);
  const makeLink = async () => {
    setBusy("link");
    try { const { data } = await api.post(`/admin/users/${u.id}/reset-link`); setLink(data); setCopied(false); }
    catch (e) { toast.error(formatApiError(e)); }
    setBusy("");
  };
  const signOut = async () => {
    setBusy("out");
    try { const { data } = await api.post(`/admin/users/${u.id}/sessions/revoke`); toast.success(`${u.email} signed out on ${data.revoked} device${data.revoked === 1 ? "" : "s"}`); window.dispatchEvent(new Event("admin:refresh")); }
    catch (e) { toast.error(formatApiError(e)); }
    setBusy("");
  };
  const copy = async () => { await navigator.clipboard.writeText(link.url); setCopied(true); toast.success("Reset link copied"); };
  return (
    <>
      <button onClick={makeLink} disabled={!!busy} data-testid={`admin-reset-link-${u.id}`} className="flex h-8 items-center gap-1.5 rounded-full border border-zinc-300 px-3 text-xs transition-colors hover:border-zinc-900">
        {busy === "link" ? <Loader2 size={13} className="animate-spin" /> : <Link2 size={13} />}Password reset link
      </button>
      <button onClick={signOut} disabled={!!busy || !u.sessions} data-testid={`admin-signout-all-${u.id}`} className="flex h-8 items-center gap-1.5 rounded-full border border-zinc-300 px-3 text-xs transition-colors hover:border-zinc-900 disabled:opacity-40">
        {busy === "out" ? <Loader2 size={13} className="animate-spin" /> : <LogOut size={13} />}Sign out everywhere
      </button>
      <Dialog open={!!link} onOpenChange={(o) => !o && setLink(null)}>
        <DialogContent className="max-w-lg" data-testid="admin-reset-link-dialog" aria-describedby={undefined}>
          <DialogHeader><DialogTitle className="font-heading text-2xl">Password reset link</DialogTitle></DialogHeader>
          <p className="-mt-2 text-sm text-zinc-600">Send this to <span className="font-mono text-xs">{u.email}</span> privately (text, chat, in person). It works once and expires in 24 hours; their current sessions are signed out when they use it.</p>
          <div className="flex min-w-0 items-center gap-2">
            <code className="min-w-0 flex-1 break-all rounded-md bg-zinc-100 px-3 py-2 font-mono text-[11px]" data-testid="admin-reset-link-url">{link?.url}</code>
            <button onClick={copy} data-testid="admin-reset-link-copy" className="flex h-9 items-center gap-1.5 rounded-full bg-zinc-900 px-4 text-xs text-white">{copied ? <Check size={13} /> : <Copy size={13} />}{copied ? "Copied" : "Copy"}</button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};
